import { properties } from '@Common/Core/Properties';
import * as commonUtils from '@Common/Helpers/CommonUtils';
import pointerHelper from '@Common/Helpers/PointerHelper';
import { GeometryPoint } from '@InstanceFactory';

import * as scrollLib from 'iscroll/build/iscroll-probe';
import { enumerable } from '@Common/Core/CoreDecorator';
import { memoize } from '@Common/Core/MemoizeDecorator';

export interface IScrollSettings {
    scrollX?: boolean; // true
    scrollY?: boolean; // true
    scrollbarX?: boolean; // true
    scrollbarY?: boolean; // true
    scrollbars?: boolean; // 'custom'
    fadeScrollbars?: boolean; // false

    allowDragScroll?: boolean; // false
    scrollToSelection?: boolean; // false

    disableTranslate?: boolean; // false
    canScroll?: () => boolean; // _.stubTrue

    onScrolledToEnd?: () => void; // noop
    onScrollStart?: () => void; // noop
    onScrollMove?: (scroll: Scroll, delta: { deltaX: number; deltaY: number }) => void; // noop

    rightPadding?: boolean; // true

    overrideCaret?: any; // null
    scroller?: any; // null

    endSensitivity?: number; // 10

    overlayedScroll?: boolean; // false
}

interface IScrollUtils {
    offset(el: HTMLElement): any;

    addEvent(el: HTMLElement | Window, type: string, fn: () => any, capture?: boolean): void;

    removeEvent(el: HTMLElement | Window, type: string, fn: () => any, capture?: boolean): void;

    prefixPointerEvent(pointerEvent: string): string;

    getTime(): number;

    style: { [name: string]: string };
    hasPointer: boolean;
}

export class Scroll {
    private readonly _paddingClass = 'iScrollPadding';

    private _iScroll: any;
    private readonly _settings: IScrollSettings;
    private _scrollElement: any;
    private _scrollToCallback: () => void;
    private readonly _overlay: JQuery = $('<div class="popup" />');
    private readonly _scroller: JQuery;

    private _isInEndArea = false;
    private _previousVScrollState: boolean;

    private readonly _indicatorX: any;
    private readonly _indicatorY: any;

    private _hasPadding: boolean;

    // TODO: SCALING
    constructor(element: any, settings?: IScrollSettings) {
        this._settings = _.defaults({}, settings, {
            bounceTime: 300,
            disableMouse: true,
            disableTouch: true,
            disablePointer: settings && settings.allowDragScroll ? false : !pointerHelper.hasTouch,
            preventDefault: false,
            freeScroll: true,
            mouseWheel: true,
            interactiveScrollbars: pointerHelper.hasMouse,
            fadeScrollbars: !pointerHelper.hasMouse,
            scrollbars: 'custom',
            probeType: 3,
            scrollX: true,
            scrollY: true,
            scrollbarX: true,
            scrollbarY: true,
            endSensitivity: 10,
            disableTranslate: false,
            canScroll: _.stubTrue,
            onScrolledToEnd: _.noop,
            onScrollStart: _.noop,
            onScrollMove: _.noop,
            overrideCaret: null,
            scroller: null,
            rightPadding: true,
            overlayedScroll: false,
            scrollToSelection: false
        });

        this._scrollElement = this.getElement(element);
        this._scrollElement.style.overflow = 'hidden';

        if (this._settings.scroller) {
            this._settings.scroller = this.getElement(this._settings.scroller);
        }

        this._iScroll = new scrollLib(this._scrollElement, this._settings);
        this._iScroll.on('scrollStart', this.onScrollStart.bind(this));
        this._iScroll.on('scroll', this.onScroll.bind(this));
        this._iScroll.on('scrollEnd', this.onScrollEnd.bind(this));
        this._iScroll.on('scrollCancel', this.onScrollEnd.bind(this));

        this._iScroll.on('indicatorStart', this.onIndicatorScrollStart.bind(this));
        this._iScroll.on('indicatorEnd', this.onIndicatorScrollEnd.bind(this));

        this._indicatorX = _.find(this._iScroll.indicators, i => i.options.listenX === true);
        this._indicatorY = _.find(this._iScroll.indicators, i => i.options.listenY === true);

        this._scroller = $(this._iScroll.scroller);
    }

    private getElement(element: any): HTMLElement {
        return element.nodeType ? element : element instanceof jQuery ? element[0] : document.querySelector(element);
    }

    private onScrollStart(): void {
        this._settings.onScrollStart();
    }

    private onScroll(): void {
        if (this._settings.onScrolledToEnd != null) {
            if (this._previousVScrollState === true && this.hasVerticalScrollbar() === false) {
                this._settings.onScrolledToEnd.call(this);
                this._previousVScrollState = false;
                return;
            }

            this._previousVScrollState = this.hasVerticalScrollbar();

            if (this.y < this.maxScrollY + this._settings.endSensitivity) {
                if (this._isInEndArea === false) {
                    this._isInEndArea = true;
                    this._settings.onScrolledToEnd.call(this);
                }
            } else {
                this._isInEndArea = false;
            }
        }

        this.onScrollMove();
    }

    private onScrollMove(): void {
        this._settings.onScrollMove(this, {
            deltaX: this._iScroll.deltaX,
            deltaY: this._iScroll.deltaY
        });
    }

    private onScrollEnd(): void {
        if (this._scrollToCallback) {
            this._scrollToCallback();
            this._scrollToCallback = null;
        }
    }

    private onIndicatorScrollStart(): void {
        // If indicator is moved by mouse - create blocking overlay
        this._overlay.appendTo('body');
    }

    private onIndicatorScrollEnd(): void {
        this._overlay.detach();
    }

    public refresh(callback?: () => void, bypassTimeout?: boolean): void {
        const localRefreshFn = () => {
            if (this._scrollElement != null) {
                const prevX = this.x;
                const prevY = this.y;

                this._iScroll.refresh();

                if (prevX !== this.x || prevY !== this.y) {
                    this.onScrollMove();
                }

                if (this._settings.rightPadding) {
                    if (this.hasVerticalScrollbar() && !this._hasPadding) {
                        this._scroller.addClass(this._paddingClass);
                        this._hasPadding = true;
                    } else if (!this.hasVerticalScrollbar() && this._hasPadding) {
                        this._scroller.removeClass(this._paddingClass);
                        this._hasPadding = false;
                    }
                }

                if (_.isFunction(callback)) {
                    callback();
                }
            }
        };
        if (bypassTimeout) {
            localRefreshFn.call(this);
        } else {
            setTimeout(localRefreshFn, 0);
        }
    }

    public ensureVisible(el: any, deltaX?: number, deltaY?: number, time?: number): void {
        const element = this.getElement(el);
        if (element == null) {
            return;
        }

        setTimeout(() => {
            this._iScroll?.ensureVisible(element, deltaX, deltaY, time);
        }, 0);
    }

    public setScrollbarsVisibility(isVisible?: boolean): void {
        for (const indicator of this._iScroll.indicators) {
            indicator.wrapper.classList.toggle('hidden', !isVisible);
        }
    }

    public subscribeToWheelEvent(el: any): void {
        const element = this.getElement(el);
        const wheelFunc = this._iScroll;

        let options: any = false;
        if (properties.isPassiveSupported) {
            options = { passive: false };
        }
        element.addEventListener('DOMMouseScroll', wheelFunc, options);
        element.addEventListener('mousewheel', wheelFunc, options);
    }

    public setScale(scale: number): void {
        throw new Error('Not implemented');
    }

    public getScrollPosition(): GeometryPoint {
        return {
            x: this._iScroll.x,
            y: this._iScroll.y
        };
    }

    public setScrollPosition(point: GeometryPoint): void {
        this._iScroll.scrollTo(point.x, point.y, 0, null);
    }

    public scrollTo(x: number, y: number, time?: number, relative?: boolean, callback?: () => void): void {
        if (relative) {
            x = this._iScroll.x - x;
            y = this._iScroll.y - y;
        }

        if (callback && time) {
            this._scrollToCallback = callback;
        }

        this._iScroll.scrollTo(x, y, time, null);

        if (callback && !time) {
            callback();
        }
    }

    public instantScrollTo(x: number, y: number): void {
        this._iScroll._instantScrollTo(x, y);
    }

    public scrollToEnd(time?: number): void {
        this._iScroll.scrollTo(this._iScroll.x, Math.min(this._iScroll.maxScrollY, 0), time);
    }

    public destroy(): void {
        this._scrollElement = null;
        this._iScroll?.destroy();
        this._iScroll = null;
    }

    public hasVerticalScrollbar(): boolean {
        return this._iScroll?.hasVerticalScroll;
    }

    public getVerticalScrollbarSize(): number {
        return this._indicatorY && this.hasVerticalScrollbar() ? this._indicatorY.wrapper.offsetWidth : 0;
    }

    public hasHorizontalScrollbar(): boolean {
        return this._iScroll?.hasHorizontalScroll;
    }

    public getHorizontalScrollbarSize(): number {
        return this._indicatorX && this.hasHorizontalScrollbar() ? this._indicatorX.wrapper.offsetHeight : 0;
    }

    public updateSettings(settings: IScrollSettings): void {
        _.assign(this._iScroll.options, settings);
        this._iScroll.refresh();
    }

    /**
     * Public properties
     */
    @enumerable
    public get x(): number {
        return this._iScroll.x;
    }

    @enumerable
    public get y(): number {
        return this._iScroll.y;
    }

    @enumerable
    public get maxScrollX(): number {
        return this._iScroll.maxScrollX;
    }

    @enumerable
    public get maxScrollY(): number {
        return this._iScroll.maxScrollY;
    }

    @memoize()
    public static getScrollSize(): { verticalSize: number; horizontalSize: number } {
        const element = $('<div>').css({ width: 100, height: 100 }).appendTo('body');
        const scroller = $('<div>').css({ width: 1000, height: 1000 }).appendTo(element);
        const scroll = new Scroll(element[0], { scroller });

        scroll.refresh(null, true);

        const verticalSize = scroll.getVerticalScrollbarSize();
        const horizontalSize = scroll.getHorizontalScrollbarSize();

        scroll.getHorizontalScrollbarSize();
        scroll.destroy();
        element.remove();

        return {
            verticalSize,
            horizontalSize
        };
    }
}

export namespace ScrollUtils {
    export function extendIScroll(): void {
        const utils: IScrollUtils = scrollLib.utils;
        const minScrollbarSize = 32;

        scrollLib.prototype._init = (function (func: any): any {
            return function (...args): void {
                if (this.options.scroller) {
                    this.scroller = this.options.scroller;
                    this.scrollerStyle = this.scroller.style;
                }

                const selectionInterval = {
                    horizontal: null,
                    vertical: null
                };

                this.handlers = {
                    keyDown: e => {
                        const target = this.options.overrideCaret ? this.options.overrideCaret : e.target;
                        const nodeName = target.nodeName;

                        if ((nodeName === 'INPUT' || nodeName === 'TEXTAREA' || this.options.overrideCaret) && this._checkOrigin(e)) {
                            this.ensureVisible(target, 8, 8, 0);
                        }
                    },
                    focus: e => {
                        e.currentTarget.scrollTop = 0;
                    },
                    touchMove: e => {
                        e.preventDefault();
                    },
                    selectionStart: e => {
                        utils.addEvent(window, utils.prefixPointerEvent('pointermove'), this.handlers.selectionMove);
                        utils.addEvent(window, utils.prefixPointerEvent('pointerup'), this.handlers.selectionEnd);
                    },
                    selectionMove: e => {
                        const scrollPadding = 20;
                        const selectionSpeedBase = 20;
                        const wrapperRect = this.wrapper.getBoundingClientRect();

                        const needScrollLeft = e.clientX - scrollPadding < wrapperRect.left;
                        const needScrollRight = e.clientX + scrollPadding > wrapperRect.right;
                        if (needScrollLeft) {
                            clearInterval(selectionInterval.horizontal);
                            selectionInterval.horizontal = setInterval(() => {
                                this._instantScrollTo(this.x + selectionSpeedBase / Math.max(1, e.clientX - wrapperRect.left), this.y);
                            }, 10);
                        }
                        if (needScrollRight) {
                            clearInterval(selectionInterval.horizontal);
                            selectionInterval.horizontal = setInterval(() => {
                                this._instantScrollTo(this.x - selectionSpeedBase / Math.max(1, wrapperRect.right - e.clientX), this.y);
                            }, 10);
                        }
                        if (!needScrollLeft && !needScrollRight) {
                            clearInterval(selectionInterval.horizontal);
                        }

                        const needScrollTop = e.clientY - scrollPadding < wrapperRect.top;
                        const needScrollBottom = e.clientY + scrollPadding > wrapperRect.bottom;
                        if (needScrollTop) {
                            clearInterval(selectionInterval.vertical);
                            selectionInterval.vertical = setInterval(() => {
                                this._instantScrollTo(this.x, this.y + selectionSpeedBase / Math.max(1, e.clientY - wrapperRect.top));
                            }, 10);
                        }
                        if (needScrollBottom) {
                            clearInterval(selectionInterval.vertical);
                            selectionInterval.vertical = setInterval(() => {
                                this._instantScrollTo(this.x, this.y - selectionSpeedBase / Math.max(1, wrapperRect.bottom - e.clientY));
                            }, 10);
                        }
                        if (!needScrollTop && !needScrollBottom) {
                            clearInterval(selectionInterval.vertical);
                        }
                    },
                    selectionEnd: e => {
                        clearInterval(selectionInterval.horizontal);
                        clearInterval(selectionInterval.vertical);
                        utils.removeEvent(window, utils.prefixPointerEvent('pointermove'), this.handlers.selectionMove);
                        utils.removeEvent(window, utils.prefixPointerEvent('pointerup'), this.handlers.selectionEnd);
                    }
                };

                this.on('refresh', () => {
                    const computedStyle = getComputedStyle(this.wrapper);
                    this.scrollerOffsetLeft = parseInt(computedStyle.getPropertyValue('padding-left')) || 0;
                    this.scrollerOffsetRight = parseInt(computedStyle.getPropertyValue('padding-right')) || 0;
                    this.scrollerOffsetTop = parseInt(computedStyle.getPropertyValue('padding-top')) || 0;
                    this.scrollerOffsetBottom = parseInt(computedStyle.getPropertyValue('padding-bottom')) || 0;

                    const rect = this.wrapper.getBoundingClientRect();
                    this.wrapperWidth = Math.ceil(rect.right - rect.left) - this.scrollerOffsetLeft - this.scrollerOffsetRight;
                    this.wrapperHeight = Math.ceil(rect.bottom - rect.top) - this.scrollerOffsetTop - this.scrollerOffsetBottom;

                    // SVG elements does not have offset dimensions
                    if (this.scroller.offsetWidth === undefined) {
                        const bBox = this.scroller.getBoundingClientRect();
                        this.scrollerWidth = bBox.right - bBox.left;
                        this.scrollerHeight = bBox.bottom - bBox.top;
                    } else {
                        this.scrollerWidth = this.scroller.offsetWidth;
                        this.scrollerHeight = this.scroller.offsetHeight;
                    }

                    this.maxScrollX = this.wrapperWidth - this.scrollerWidth;
                    this.maxScrollY = this.wrapperHeight - this.scrollerHeight;

                    this.hasHorizontalScroll = this.options.scrollX && this.maxScrollX < 0;
                    this.hasVerticalScroll = this.options.scrollY && this.maxScrollY < 0;

                    if (!this.hasHorizontalScroll) {
                        this.maxScrollX = 0;
                        this.scrollerWidth = this.wrapperWidth;
                    }

                    if (!this.hasVerticalScroll) {
                        this.maxScrollY = 0;
                        this.scrollerHeight = this.wrapperHeight;
                    }

                    this.wrapper.setAttribute('hasScroll', (this.hasHorizontalScroll ? 'x' : '') + (this.hasVerticalScroll ? 'y' : ''));
                });

                func.apply(this, args);
            };
        })(scrollLib.prototype._init);

        scrollLib.prototype.destroy = (function (func: any): any {
            return function (...args): void {
                func.apply(this, args);
                this._events = {};
            };
        })(scrollLib.prototype.destroy);

        scrollLib.prototype._initIndicators = (function (func: any): any {
            return function (...args): void {
                func.apply(this, args);

                /*if (this.options.fadeScrollbars) {
                    $(this.wrapper).hover(() => {
                        this.updateIndicatorsOpacity(0.5);
                    }, () => {
                        this.updateIndicatorsOpacity(0);
                    });
                }*/

                if (this.options.overlayedScroll) {
                    this.wrapper.style.visibility = 'hidden';

                    _.forEach(this.indicators, indicator => {
                        this.wrapper.parentNode.appendChild(indicator.wrapper);
                    });
                }

                _.forEach(this.indicators, indicator => {
                    utils.removeEvent(window, 'mouseup', indicator);

                    if (indicator.options.interactive) {
                        utils.removeEvent(indicator.indicator, 'touchstart', indicator);
                        utils.removeEvent(window, 'touchmove', indicator);
                        utils.removeEvent(window, 'touchend', indicator);

                        utils.removeEvent(indicator.indicator, utils.prefixPointerEvent('pointerdown'), indicator);
                        utils.removeEvent(window, utils.prefixPointerEvent('pointermove'), indicator);
                        utils.removeEvent(window, utils.prefixPointerEvent('pointerup'), indicator);
                    }

                    // indicator.wrapperStyle.opacity = this.options.fadeScrollbars ? 0 : 0.5;

                    indicator.setOpacity = value => {
                        indicator.wrapperStyle[utils.style.transitionDuration] = '500ms';

                        setTimeout(() => {
                            indicator.wrapperStyle.opacity = value;
                            indicator.visible = +value;
                        }, 0);

                        setTimeout(() => {
                            indicator.wrapperStyle[utils.style.transitionDuration] = '0ms';
                        }, 500);
                    };

                    indicator.refresh = (function (f: any): any {
                        return function (...args): void {
                            if (this.options.listenX) {
                                this.wrapperStyle.marginLeft = this.scroller.scrollerOffsetLeft + 'px';
                                this.wrapperStyle.marginRight = this.scroller.scrollerOffsetRight + 'px';
                            }

                            if (this.options.listenY) {
                                this.wrapperStyle.marginTop = this.scroller.scrollerOffsetTop + 'px';
                                this.wrapperStyle.marginBottom = this.scroller.scrollerOffsetBottom + 'px';
                            }

                            const oldHasHorizontal = this.scroller.hasHorizontalScroll;
                            const oldHasVertical = this.scroller.hasVerticalScroll;

                            this.scroller.hasHorizontalScroll = this.scroller.hasHorizontalScroll && this.scroller.options.scrollbarX;
                            this.scroller.hasVerticalScroll = this.scroller.hasVerticalScroll && this.scroller.options.scrollbarY;

                            f.apply(this, args);

                            this.scroller.hasHorizontalScroll = oldHasHorizontal;
                            this.scroller.hasVerticalScroll = oldHasVertical;

                            if (this.scroller.options.overlayedScroll) {
                                if (this.options.listenX) {
                                    if (this._defaultOffset === undefined) {
                                        this._defaultOffset = this.wrapper.offsetLeft;
                                        this._defaultSize = this.wrapper.offsetHeight;
                                    }

                                    if (properties.browser.mozilla) {
                                        this.wrapperStyle.width = 'auto';
                                    }
                                    const offsetRight = parseInt(getComputedStyle(this.wrapper).right);

                                    this.wrapperStyle.width = this.scroller.wrapperWidth - (this._defaultOffset + offsetRight) + 'px';
                                    this.wrapperStyle.left = this.scroller.wrapper.offsetLeft + this._defaultOffset + 'px';
                                    this.wrapperStyle.top = this.scroller.wrapper.offsetTop + this.scroller.wrapperHeight - this._defaultSize + 'px';
                                }

                                if (this.options.listenY) {
                                    if (this._defaultOffset === undefined) {
                                        this._defaultOffset = this.wrapper.offsetTop;
                                        this._defaultSize = this.wrapper.offsetWidth;
                                    }

                                    if (properties.browser.mozilla) {
                                        this.wrapperStyle.height = 'auto';
                                    }
                                    const offsetBottom = parseInt(getComputedStyle(this.wrapper).bottom);

                                    this.wrapperStyle.height = this.scroller.wrapperHeight - (this._defaultOffset + offsetBottom) + 'px';
                                    this.wrapperStyle.top = this.scroller.wrapper.offsetTop + this._defaultOffset + 'px';
                                    this.wrapperStyle.left = this.scroller.wrapper.offsetLeft + this.scroller.wrapperWidth - this._defaultSize + 'px';
                                }
                            }

                            this.wrapperStyle.display = this.indicatorStyle.display;

                            if (this.options.listenX) {
                                this.wrapperWidth = this.wrapper.clientWidth;

                                if (this.options.resize) {
                                    this.indicatorWidth = Math.max(
                                        Math.round((this.wrapperWidth * this.wrapperWidth) / (this.scroller.scrollerWidth || this.wrapperWidth || 1)),
                                        minScrollbarSize
                                    );
                                    this.indicatorStyle.width = this.indicatorWidth + 'px';
                                }

                                this.maxPosX = this.wrapperWidth - this.indicatorWidth;

                                if (this.options.shrink === 'clip') {
                                    this.minBoundaryX = -this.indicatorWidth + minScrollbarSize;
                                    this.maxBoundaryX = this.wrapperWidth - minScrollbarSize;
                                } else {
                                    this.maxBoundaryX = this.maxPosX;
                                }

                                this.sizeRatioX = this.options.speedRatioX || (this.scroller.maxScrollX && this.maxPosX / this.scroller.maxScrollX);
                            }

                            if (this.options.listenY) {
                                this.wrapperHeight = this.wrapper.clientHeight;

                                if (this.options.resize) {
                                    this.indicatorHeight = Math.max(
                                        Math.round(
                                            (this.wrapperHeight * this.wrapperHeight) / (this.scroller.scrollerHeight || this.wrapperHeight || 1)
                                        ),
                                        minScrollbarSize
                                    );
                                    this.indicatorStyle.height = this.indicatorHeight + 'px';
                                }

                                this.maxPosY = this.wrapperHeight - this.indicatorHeight;

                                if (this.options.shrink === 'clip') {
                                    this.minBoundaryY = -this.indicatorHeight + minScrollbarSize;
                                    this.maxBoundaryY = this.wrapperHeight - minScrollbarSize;
                                } else {
                                    this.maxBoundaryY = this.maxPosY;
                                }

                                this.sizeRatioY = this.options.speedRatioY || (this.scroller.maxScrollY && this.maxPosY / this.scroller.maxScrollY);
                            }

                            this.updatePosition();
                        };
                    })(indicator.refresh);

                    indicator._start = (function (f: any): any {
                        return function (...args): void {
                            utils.addEvent(window, 'mouseup', this);

                            f.apply(this, args);
                            this.scroller._execEvent('indicatorStart');
                            // this.setOpacity(1);
                        };
                    })(indicator._start);

                    indicator._end = (function (f: any): any {
                        return function (...args): void {
                            utils.removeEvent(window, 'mouseup', this);

                            if (this.initiated) {
                                this.scroller._execEvent('indicatorEnd');
                                // this.setOpacity(this.options.fadeScrollbars ? 0 : 0.5);
                            }
                            f.apply(this, args);
                        };
                    })(indicator._end);
                });
            };
        })(scrollLib.prototype._initIndicators);

        scrollLib.prototype.ensureVisible = function (el: any, deltaX?: number, deltaY?: number, time?: number): void {
            deltaX = deltaX || 0;
            deltaY = deltaY || 0;

            const pos = utils.offset(el);

            pos.left -= this.wrapperOffset.left;
            pos.top -= this.wrapperOffset.top;

            if (pos.left > this.x - deltaX) {
                pos.left += deltaX;
            } else if (this.x - this.wrapperWidth > pos.left - el.offsetWidth - deltaX) {
                pos.left += this.wrapperWidth - deltaX - el.offsetWidth;
            } else {
                pos.left = this.x;
            }

            if (pos.top > this.y - deltaY) {
                pos.top += deltaY;
            } else if (this.y - this.wrapperHeight > pos.top - el.offsetHeight - deltaY) {
                pos.top += this.wrapperHeight - deltaY - el.offsetHeight;
            } else {
                pos.top = this.y;
            }

            pos.left = pos.left > 0 ? 0 : pos.left < this.maxScrollX ? this.maxScrollX : pos.left;
            pos.top = pos.top > 0 ? 0 : pos.top < this.maxScrollY ? this.maxScrollY : pos.top;
            time = time === undefined ? Math.max(Math.abs(this.x - pos.left), Math.abs(this.y - pos.top)) : time;

            this.scrollTo(pos.left, pos.top, time);
        };

        scrollLib.prototype._getScrollTarget = function (target: HTMLElement | any): HTMLElement {
            while (target && !target.hasAttribute('hasScroll')) {
                target = target.parentNode;
            }

            return target;
        };

        scrollLib.prototype._checkOrigin = function (e: PointerEvent | any): boolean {
            let target = this._getScrollTarget(e.target);

            if (e.skipScroll) {
                target = this._getScrollTarget(target.parentNode) || target;
            }

            if (target === null) {
                return true;
            }

            // Clear popups if scrolling is not from popup itself
            if (this.wrapper === target && $(this.wrapper).parents('.popup-control').length === 0 && !(e instanceof KeyboardEvent)) {
                commonUtils.clearPopups();
            }

            if (e.target.ownerDocument.defaultView === window && this.wrapper !== target) {
                return false;
            }
            return true;
        };

        scrollLib.prototype._preventScroll = function (e: PointerEvent | any): boolean {
            let target = e.target;
            const prohibitedClassList = ['iScrollIndicator', 'selection-container', 'view-resizing', 'ui-draggable-handle'];

            while (target && target !== document.body && !prohibitedClassList.some(s => target.classList.contains(s))) {
                target = target.parentNode;
            }

            return target !== document.body;
        };

        scrollLib.prototype._stop = function (e: PointerEvent): void {
            this.options.momentum = !this.options.momentum;
            this._end(
                $.extend({}, e, {
                    pointerId: this.pointerDownEvent.pointerId
                })
            );
            this.options.momentum = !this.options.momentum;
        };

        scrollLib.prototype._wheel = (function (func: any): any {
            return function (e: WheelEvent | any): void {
                if (e.shiftKey && this.hasHorizontalScroll && this.hasVerticalScroll) {
                    if ('deltaX' in e) {
                        Object.defineProperties(e, {
                            deltaX: { value: e.deltaY },
                            deltaY: { value: 0 }
                        });
                    } else if ('wheelDeltaX' in e) {
                        Object.defineProperties(e, {
                            wheelDeltaX: { value: e.wheelDeltaY },
                            wheelDeltaY: { value: 0 }
                        });
                    } else if ('wheelDelta' in e) {
                        Object.defineProperties(e, {
                            wheelDeltaX: { value: e.wheelDelta },
                            wheelDelta: { value: 0 }
                        });
                    } else if ('detail' in e) {
                        Object.defineProperties(e, {
                            wheelDeltaX: { value: -e.detail * 40 },
                            detail: { value: 0 }
                        });
                    }
                }

                if (!e.hadVerticalScroll || e.shiftKey || this.hasVerticalScroll == e.hadVerticalScroll) {
                    func.call(this, e);
                }

                if (this.deltaX || this.deltaY) {
                    e.stopPropagation();
                }

                Object.defineProperties(e, {
                    hadVerticalScroll: {
                        value: this.hasVerticalScroll,
                        configurable: true
                    }
                });
            };
        })(scrollLib.prototype._wheel);

        scrollLib.prototype._start = (function (func: any): any {
            return function (e: PointerEvent | any): void {
                if (!e.isPrimary && !e.skipScroll) {
                    e.skipScroll = true;
                    this._stop(e);
                    return;
                }

                // To prevent from scrolling by dragging content with mouse
                if (!this.options.allowDragScroll && pointerHelper.getPointerType(e) === pointerHelper.pointerType.mouse) {
                    return;
                }

                if (!this._checkOrigin(e) || !this.options.canScroll() || this._preventScroll(e)) {
                    return;
                }

                const def: JQueryDeferred<void> = $(e.currentTarget).data('scrollDeferred');
                if (def != null) {
                    def.done(() => {
                        $(e.currentTarget).removeData('scrollDeferred');
                        this._start.call(this, e);
                    });
                    return;
                }

                func.call(this, e);

                if (this.initiated) {
                    this.pointerDownEvent = e;

                    let target = e.target;
                    while (target.nodeType !== 1) {
                        target = target.parentNode;
                    }

                    if (target.tagName === 'SELECT' || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
                        utils.addEvent(window, 'touchmove', this.handlers.touchMove);
                    }

                    this._initPointerEvents();
                }
            };
        })(scrollLib.prototype._start);

        scrollLib.prototype._move = (function (func: any): any {
            return function (e: PointerEvent): void {
                if (e.pointerId !== this.pointerDownEvent.pointerId || this.pointerDownEvent.preventClick) {
                    if (!this.pointerDownEvent.skipScroll) {
                        this._stop(e);
                    }
                    return;
                }

                func.call(this, e);
            };
        })(scrollLib.prototype._move);

        scrollLib.prototype._end = (function (func: any): any {
            return function (e: PointerEvent): void {
                if (e.pointerId !== this.pointerDownEvent.pointerId) {
                    return;
                }
                this.pointerDownEvent = null;

                utils.removeEvent(window, 'touchmove', this.handlers.touchMove);
                this._initPointerEvents(true);

                func.call(this, e);
            };
        })(scrollLib.prototype._end);

        scrollLib.prototype._initEvents = (function (func: any): any {
            return function (remove: boolean): void {
                func.call(this, remove);

                const eventType = remove ? utils.removeEvent : utils.addEvent;

                this._initPointerEvents(true);

                if (utils.hasPointer && this.options.scrollToSelection) {
                    eventType(this.wrapper, utils.prefixPointerEvent('pointerdown'), this.handlers.selectionStart);
                }

                eventType(this.wrapper, 'keydown', this.handlers.keyDown);
                eventType(this.wrapper, 'focusin', this.handlers.focus);
                eventType(this.wrapper, 'focus', this.handlers.focus, true);
            };
        })(scrollLib.prototype._initEvents);

        scrollLib.prototype._translate = (function (func: any): any {
            return function (x: number, y: number): void {
                this.deltaX = this.x - x;
                this.deltaY = this.y - y;

                if (this.options.disableTranslate) {
                    this.x = x;
                    this.y = y;

                    if (this.indicators) {
                        for (let i = this.indicators.length; i--; ) {
                            this.indicators[i].updatePosition();
                        }
                    }
                } else {
                    func.call(this, x, y);
                }
            };
        })(scrollLib.prototype._translate);

        scrollLib.prototype.resetPosition = (function (func: any): any {
            return function (time: number): boolean {
                if (this.pointerDownEvent != null && time === undefined) {
                    return false;
                }
                return func.call(this, time);
            };
        })(scrollLib.prototype.resetPosition);

        scrollLib.prototype._initPointerEvents = function (remove: boolean): void {
            const eventType = remove ? utils.removeEvent : utils.addEvent;
            const target = this.options.bindToWrapper ? this.wrapper : window;

            if (utils.hasPointer && !this.options.disablePointer) {
                eventType(target, utils.prefixPointerEvent('pointermove'), this);
                eventType(target, utils.prefixPointerEvent('pointercancel'), this);
                eventType(target, utils.prefixPointerEvent('pointerup'), this);
            }
        };

        scrollLib.prototype._instantScrollTo = function (x: number, y: number): void {
            x = x > 0 ? 0 : x < this.maxScrollX ? this.maxScrollX : x;
            y = y > 0 ? 0 : y < this.maxScrollY ? this.maxScrollY : y;
            this._translate(x, y);
        };

        scrollLib.prototype._rAF = (function (): (callback: any) => any {
            for (const vendor of ['r', 'webkitR', 'mozR', 'oR', 'msR']) {
                if (vendor + 'equestAnimationFrame' in window) {
                    return window[vendor + 'equestAnimationFrame'].bind(window);
                }
            }

            return callback => window.setTimeout(callback, 1000 / 60);
        })();

        scrollLib.prototype.scrollTo = (function (func: any): any {
            return function (x: number, y: number, time?: number, easing?: any): void {
                if (this.isAnimating) {
                    this._onStopAnimation = () => {
                        this._onStopAnimation = null;
                        this.scrollTo(x, y, time, easing);
                    };
                } else {
                    func.call(this, x, y, time, easing);
                }
            };
        })(scrollLib.prototype.scrollTo);

        scrollLib.prototype._animate = function (destX: number, destY: number, duration: number, easingFn: any): void {
            const startX = this.x;
            const startY = this.y;
            const startTime = utils.getTime();
            const destTime = startTime + duration;

            const step = () => {
                let now = utils.getTime();

                if (this._onStopAnimation) {
                    this.isAnimating = false;
                    this._onStopAnimation && this._onStopAnimation();
                    return;
                }

                if (now >= destTime) {
                    this.isAnimating = false;
                    this._translate(destX, destY);

                    if (!this.resetPosition(this.options.bounceTime)) {
                        this._execEvent('scrollEnd');
                    }

                    return;
                }

                now = (now - startTime) / duration;
                const easing = easingFn(now);
                const newX = (destX - startX) * easing + startX;
                const newY = (destY - startY) * easing + startY;
                this._translate(newX, newY);

                if (this.isAnimating) {
                    this._rAF(step);
                }

                if (this.options.probeType === 3) {
                    this._execEvent('scroll');
                }
            };

            this.isAnimating = true;
            step();
        };
    }
}
